import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share2, Image as ImageIcon, Camera } from "lucide-react";
import { formatTime, modeById } from "@/lib/roninBox";
import { toast } from "sonner";

/**
 * Generates a workout-summary image with stats baked in for IG/TikTok sharing.
 * Aspect ratios: 1:1 (1080x1080), 4:5 (1080x1350), 9:16 (1080x1920)
 *
 * Props: { result: {mode, rounds, roundsCompleted, elapsedSec, activeSec, punches, calories}, onClose }
 */
const RATIOS = [
  { id: "1:1", w: 1080, h: 1080, label: "Square" },
  { id: "4:5", w: 1080, h: 1350, label: "Portrait" },
  { id: "9:16", w: 1080, h: 1920, label: "Story" },
];

export function RoninBoxShare({ result, onClose }) {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [ratio, setRatio] = useState(RATIOS[1]); // default 4:5
  const [bgImage, setBgImage] = useState(null); // HTMLImageElement
  const [bgDataUrl, setBgDataUrl] = useState(null);

  // Re-draw whenever the ratio or background image changes. `draw` reads `result` (fixed
  // per workout) and is recreated each render, so it always closes over fresh values —
  // intentionally excluded from deps to avoid re-wrapping its canvas helpers.
  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratio, bgImage]);

  const draw = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = ratio.w;
    c.height = ratio.h;
    const ctx = c.getContext("2d");

    // 1) Background — photo (object-cover) or solid black
    if (bgImage) {
      drawCover(ctx, bgImage, ratio.w, ratio.h);
      // Dark gradient overlay so text remains readable
      const grad = ctx.createLinearGradient(0, 0, 0, ratio.h);
      grad.addColorStop(0, "rgba(0,0,0,0.45)");
      grad.addColorStop(0.5, "rgba(0,0,0,0.25)");
      grad.addColorStop(1, "rgba(0,0,0,0.85)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, ratio.w, ratio.h);
    } else {
      ctx.fillStyle = "#050505";
      ctx.fillRect(0, 0, ratio.w, ratio.h);
      // subtle radial accent
      const rg = ctx.createRadialGradient(ratio.w * 0.5, ratio.h * 0.3, 0, ratio.w * 0.5, ratio.h * 0.3, ratio.w * 0.8);
      rg.addColorStop(0, "rgba(255,51,31,0.18)");
      rg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = rg;
      ctx.fillRect(0, 0, ratio.w, ratio.h);
    }

    // 2) Top wordmark — "RONIN BOX"
    ctx.fillStyle = "#FF331F";
    ctx.font = `900 ${Math.round(ratio.w * 0.038)}px Helvetica, Arial, sans-serif`;
    ctx.textBaseline = "top";
    ctx.textAlign = "left";
    const pad = Math.round(ratio.w * 0.07);
    ctx.fillText("RONIN BOX", pad, pad);

    // Sub-label
    ctx.fillStyle = "#a1a1aa";
    ctx.font = `700 ${Math.round(ratio.w * 0.018)}px Helvetica, Arial, sans-serif`;
    ctx.fillText("MOBILE TRAINING PARTNER", pad, pad + Math.round(ratio.w * 0.052));

    // 3) Mode + date — top-right
    const modeMeta = modeById(result.mode);
    const dateStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    ctx.textAlign = "right";
    ctx.fillStyle = "#fff";
    ctx.font = `800 ${Math.round(ratio.w * 0.024)}px Helvetica, Arial, sans-serif`;
    ctx.fillText(modeMeta.label.toUpperCase(), ratio.w - pad, pad);
    ctx.fillStyle = "#a1a1aa";
    ctx.font = `600 ${Math.round(ratio.w * 0.018)}px Helvetica, Arial, sans-serif`;
    ctx.fillText(dateStr, ratio.w - pad, pad + Math.round(ratio.w * 0.04));

    // 4) Big punch/calorie hero — centered upper-middle
    ctx.textAlign = "center";
    ctx.fillStyle = "#fff";
    const heroY = Math.round(ratio.h * 0.42);
    ctx.font = `900 ${Math.round(ratio.w * 0.32)}px Helvetica, Arial, sans-serif`;
    ctx.fillText(String(result.punches), ratio.w / 2, heroY);
    ctx.fillStyle = "#FF331F";
    ctx.font = `800 ${Math.round(ratio.w * 0.028)}px Helvetica, Arial, sans-serif`;
    ctx.textBaseline = "top";
    ctx.fillText("PUNCHES THROWN", ratio.w / 2, heroY + Math.round(ratio.w * 0.32) - Math.round(ratio.w * 0.005));

    // 5) Stats row — Time / Rounds / Calories — bottom block
    const blockY = Math.round(ratio.h * 0.74);
    const blockH = Math.round(ratio.h * 0.13);
    // Card background
    ctx.fillStyle = "rgba(10,10,10,0.85)";
    ctx.fillRect(pad, blockY, ratio.w - pad * 2, blockH);
    // Top accent
    ctx.fillStyle = "#FF331F";
    ctx.fillRect(pad, blockY, ratio.w - pad * 2, 6);

    const cols = [
      { label: "TIME", value: formatTime(result.activeSec) },
      { label: "ROUNDS", value: `${result.roundsCompleted}/${result.rounds}` },
      { label: "CALORIES", value: String(result.calories) },
    ];
    const colW = (ratio.w - pad * 2) / cols.length;
    cols.forEach((col, i) => {
      const cx = pad + colW * i + colW / 2;
      ctx.fillStyle = "#fff";
      ctx.font = `900 ${Math.round(ratio.w * 0.06)}px Helvetica, Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(col.value, cx, blockY + blockH * 0.62);
      ctx.fillStyle = "#a1a1aa";
      ctx.font = `700 ${Math.round(ratio.w * 0.018)}px Helvetica, Arial, sans-serif`;
      ctx.fillText(col.label, cx, blockY + blockH * 0.88);
    });

    // 6) Footer — site URL
    ctx.fillStyle = "#fff";
    ctx.font = `800 ${Math.round(ratio.w * 0.025)}px Helvetica, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("BETTERTHROUGHBOXING.COM", ratio.w / 2, ratio.h - Math.round(ratio.h * 0.04));
    // tiny red bar above footer
    ctx.fillStyle = "#FF331F";
    const barW = Math.round(ratio.w * 0.08);
    ctx.fillRect(ratio.w / 2 - barW / 2, ratio.h - Math.round(ratio.h * 0.07), barW, 3);
  };

  // Centered cover-fit
  const drawCover = (ctx, img, w, h) => {
    const ir = img.width / img.height;
    const cr = w / h;
    let dw, dh, dx, dy;
    if (ir > cr) {
      dh = h; dw = h * ir; dx = (w - dw) / 2; dy = 0;
    } else {
      dw = w; dh = w / ir; dx = 0; dy = (h - dh) / 2;
    }
    ctx.drawImage(img, dx, dy, dw, dh);
  };

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick a photo"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => { setBgImage(img); setBgDataUrl(reader.result); };
      img.onerror = () => toast.error("Could not read that image");
      img.src = reader.result;
    };
    reader.onerror = () => toast.error("Could not read that file");
    reader.readAsDataURL(f);
  };

  const clearImage = () => { setBgImage(null); setBgDataUrl(null); };

  const dataUrl = () => canvasRef.current?.toDataURL("image/jpeg", 0.92);

  const download = () => {
    const url = dataUrl(); if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `ronin-box-${result.mode}-${Date.now()}.jpg`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Image saved — share it anywhere.");
  };

  const share = async () => {
    const canvas = canvasRef.current; if (!canvas) return;
    if (navigator.share && navigator.canShare) {
      try {
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
        const file = new File([blob], `ronin-box-${result.mode}-${Date.now()}.jpg`, { type: "image/jpeg" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Ronin Box workout",
            text: `Just finished a ${result.activeSec >= 60 ? Math.round(result.activeSec / 60) + ' min' : result.activeSec + 's'} ${modeById(result.mode).label.toLowerCase()} session — ${result.punches} punches, ${result.calories} kcal. betterthroughboxing.com`,
          });
          return;
        }
      } catch (e) {
        // user-cancelled or unsupported — fall through to download
        if (e?.name === "AbortError") return;
      }
    }
    // Fallback — just download
    download();
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-md p-4 sm:p-6 overflow-y-auto" data-testid="ronin-share">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Share your work</p>
            <h2 className="font-display text-2xl text-white uppercase tracking-tight">Workout Card</h2>
          </div>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="ronin-share-close">Close</Button>
        </div>

        {/* Aspect ratio toggles */}
        <div className="flex items-center gap-2 mb-4">
          {RATIOS.map((r) => (
            <button
              key={r.id}
              onClick={() => setRatio(r)}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest font-bold border rounded-sm transition-colors ${ratio.id === r.id ? "border-[#FF331F] bg-[#FF331F]/10 text-white" : "border-[#27272A] bg-[#0A0A0A] text-zinc-400 hover:text-white"}`}
              data-testid={`ronin-ratio-${r.id}`}
            >
              {r.id} <span className="ml-1 text-zinc-500 normal-case tracking-normal">{r.label}</span>
            </button>
          ))}
        </div>

        {/* Canvas preview */}
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3 sm:p-4 flex items-center justify-center mb-4">
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-[70vh] h-auto"
            style={{ aspectRatio: `${ratio.w}/${ratio.h}` }}
            data-testid="ronin-share-canvas"
          />
        </div>

        {/* Photo controls */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button onClick={() => fileInputRef.current?.click()} className="border border-[#27272A] bg-[#0A0A0A] hover:bg-[#1a1a1a] py-3 rounded-sm text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 text-zinc-300" data-testid="ronin-share-upload">
            <Camera className="h-4 w-4" /> {bgImage ? "Replace photo" : "Add background photo"}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" hidden onChange={onUpload} />
          <button onClick={clearImage} disabled={!bgImage} className="border border-[#27272A] bg-[#0A0A0A] hover:bg-[#1a1a1a] disabled:opacity-40 py-3 rounded-sm text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 text-zinc-300" data-testid="ronin-share-clear">
            <ImageIcon className="h-4 w-4" /> Use solid background
          </button>
        </div>

        {/* Primary actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={share} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs py-6 rounded-sm" data-testid="ronin-share-share">
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
          <Button onClick={download} variant="outline" className="border-[#27272A] bg-[#0A0A0A] text-zinc-200 hover:bg-[#1a1a1a] font-bold uppercase tracking-widest text-xs py-6 rounded-sm" data-testid="ronin-share-download">
            <Download className="h-4 w-4 mr-2" /> Download
          </Button>
        </div>

        {bgDataUrl && (
          <p className="text-[10px] text-zinc-600 text-center mt-3">Background image stays on your device — never uploaded.</p>
        )}
      </div>
    </div>
  );
}
